/**
 * 前端级媒体防护（挡普通用户）：
 * - video/audio：隐藏控件下载、禁画中画
 * - video/audio/img：禁右键菜单、禁拖拽另存
 * 说明：无法阻止开发者工具/抓包拿到直链，需后端签名才能提高难度。
 */

const MEDIA_SELECTOR = 'video, audio, img'

/** 标记允许浏览器原生 controls 显示下载（如 TikTok AI 生成结果预览） */
const ALLOW_DOWNLOAD_ATTR = 'data-allow-media-download'

function allowsMediaDownload(el: HTMLElement): boolean {
  return el.hasAttribute(ALLOW_DOWNLOAD_ATTR)
}

function hardenMediaElement(el: Element) {
  if (!(el instanceof HTMLElement)) return

  if (el instanceof HTMLVideoElement || el instanceof HTMLAudioElement) {
    const current = (el.getAttribute('controlslist') || '').toLowerCase()
    const parts = new Set(current.split(/\s+/).filter(Boolean))
    if (allowsMediaDownload(el)) {
      parts.delete('nodownload')
    } else {
      parts.add('nodownload')
    }
    parts.add('noplaybackrate')
    const next = Array.from(parts).join(' ')
    // 仅在值变化时写入，避免无意义 DOM 更新
    if (current !== next) {
      el.setAttribute('controlslist', next)
    }
    if (el instanceof HTMLVideoElement && !el.disablePictureInPicture) {
      el.disablePictureInPicture = true
    }
  }

  if (
    (el instanceof HTMLImageElement ||
      el instanceof HTMLVideoElement ||
      el instanceof HTMLAudioElement) &&
    el.draggable
  ) {
    el.draggable = false
    el.setAttribute('draggable', 'false')
    el.style.setProperty('-webkit-user-drag', 'none')
  }
}

function hardenTree(root: ParentNode) {
  if (root instanceof Element && root.matches?.(MEDIA_SELECTOR)) {
    hardenMediaElement(root)
  }
  root.querySelectorAll?.(MEDIA_SELECTOR).forEach(hardenMediaElement)
}

function isProtectedMediaTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(target.closest(MEDIA_SELECTOR))
}

export function setupProtectMedia() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const onContextMenu = (e: MouseEvent) => {
    if (isProtectedMediaTarget(e.target)) {
      e.preventDefault()
    }
  }

  const onDragStart = (e: DragEvent) => {
    if (isProtectedMediaTarget(e.target)) {
      e.preventDefault()
    }
  }

  document.addEventListener('contextmenu', onContextMenu, true)
  document.addEventListener('dragstart', onDragStart, true)

  hardenTree(document)

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element || node instanceof DocumentFragment) {
            hardenTree(node)
          }
        })
      } else if (
        mutation.type === 'attributes' &&
        mutation.target instanceof Element &&
        mutation.target.matches(MEDIA_SELECTOR)
      ) {
        // 只监听 src/controls；不监听 controlslist，防止自身 setAttribute 形成循环
        hardenMediaElement(mutation.target)
      }
    }
  })

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'controls']
  })
}
